/**
 * SectionHeading Component - Foundational Primitive #3
 *
 * Centered, uppercase, letter-spaced section heading with optional decorative rule.
 * Uses CSS primitive classes from:
 * - 03-section-heading.css for heading and rule styling
 *
 * Per v3.3 theme specification:
 * - NO emojis (the decorative rule IS the accent)
 * - NO bold (weight 400)
 * - Small font size (13px)
 * - Always centered
 * - Always uppercase with wide letter spacing
 *
 * Features:
 * - Serif display font for elegance
 * - Optional decorative gold rule with diamond
 * - Consistent spacing and alignment
 */

import React from 'react';
import '../../theme-components/03-section-heading.css';

export interface SectionHeadingProps {
  /**
   * The heading text (will be automatically uppercased by CSS in 'default' variant)
   */
  children: React.ReactNode;

  /**
   * Whether to show the decorative rule below the heading
   * @default true
   */
  showRule?: boolean;

  /**
   * Variant of the heading style
   * - 'default': Small (13px), uppercase, letter-spaced, centered
   * - 'large': Larger (18px), normal case, medium weight, centered
   * @default 'default'
   */
  variant?: 'default' | 'large';

  /**
   * Optional className for additional styling
   */
  className?: string;

  /**
   * Semantic heading level (h1-h6)
   * @default 'h2'
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const SectionHeading: React.FC<SectionHeadingProps> = ({
  children,
  showRule = true,
  variant = 'default',
  className = '',
  as: Tag = 'h2'
}) => {
  const headingClass = variant === 'large' ? 'section-heading-large' : 'section-heading';

  return (
    <div className={`section-heading-group ${className}`}>
      <Tag className={headingClass}>{children}</Tag>
      {showRule && (
        <div className="decorative-rule" aria-hidden="true">
          <span className="rule-line"></span>
          <span className="rule-diamond"></span>
          <span className="rule-line"></span>
        </div>
      )}
    </div>
  );
};

export default SectionHeading;
